import { Badge, Box, Card, Center, HStack, Image, Text } from '@chakra-ui/react'
import { Link } from 'react-router'
import type { Agenda } from '@/services/agendas'
import { ROLE_CONFIG } from '@/utils/agendaRoles'
import { firstAndLastName } from '@/utils/string'
import logo from '@/assets/stagebook_logo.webp'

type AgendaCardProps = {
  agenda: Agenda
  view: 'grid' | 'list'
}

const AgendaCard = ({ agenda, view }: AgendaCardProps) => (
  <Card.Root
    variant="outline"
    asChild
    cursor="pointer"
    w="full"
    overflow="hidden"
    _hover={{ shadow: 'sm' }}
  >
    <Link to={`/painel/agendas/${agenda.id}`}>
      {view === 'grid' ? (
        <>
          {agenda.image_path ? (
            <Image
              src={agenda.image_path}
              alt={agenda.name}
              w="full"
              h="48"
              objectFit="cover"
            />
          ) : (
            <Center w="full" h="48" bg="gray.200">
              <Image src={logo} alt="StageBook" maxH="16" maxW="32" objectFit="contain" opacity={0.7} />
            </Center>
          )}
          <Card.Body>
            <Box minW={0}>
              <HStack gap={3} mb={2}>
                <Badge colorPalette={agenda.is_active ? 'green' : 'gray'}>
                  {agenda.is_active ? 'Ativa' : 'Inativa'}
                </Badge>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={ROLE_CONFIG[agenda.role]?.color || 'fg.muted'}
                  letterSpacing="wide"
                >
                  {ROLE_CONFIG[agenda.role]?.label || ''}
                </Text>
              </HStack>
              <Card.Title fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.3} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{agenda.name}</Card.Title>
              {agenda.owner && (
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="fg.muted">
                  Por: {firstAndLastName(agenda.owner.name)}
                </Text>
              )}
            </Box>
          </Card.Body>
        </>
      ) : (
        <HStack gap={0} align="stretch">
          {agenda.image_path ? (
            <Image
              src={agenda.image_path}
              alt={agenda.name}
              w={{ base: '20', md: '24' }}
              h={{ base: 'auto', md: '24' }}
              flexShrink={0}
              objectFit="cover"
              display="block"
              alignSelf={{ base: 'stretch', md: 'auto' }}
            />
          ) : (
            <Center w={{ base: '20', md: '24' }} h={{ base: 'auto', md: '24' }} flexShrink={0} bg="gray.200" alignSelf={{ base: 'stretch', md: 'auto' }}>
              <Image src={logo} alt="StageBook" maxW="14" objectFit="contain" opacity={0.7} />
            </Center>
          )}
          <Card.Body p={{ base: 2, md: 4 }} minW={0}>
            <Box
              display="flex"
              flexDirection={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              h="full"
              gap={{ base: 1, md: 0 }}
              minW={0}
            >
              <Box minW={0} overflow="hidden">
                <Card.Title fontSize={{ base: 'sm', md: 'lg' }} lineHeight={1.3} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{agenda.name}</Card.Title>
                {agenda.owner && (
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color="fg.muted">
                    Por: {firstAndLastName(agenda.owner.name)}
                  </Text>
                )}
                <HStack gap={2} mt={1} flexWrap="wrap" display={{ base: 'flex', md: 'none' }}>
                  <Badge colorPalette={agenda.is_active ? 'green' : 'gray'} size="sm">
                    {agenda.is_active ? 'Ativa' : 'Inativa'}
                  </Badge>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    textTransform="uppercase"
                    color={ROLE_CONFIG[agenda.role]?.color || 'fg.muted'}
                    letterSpacing="wide"
                  >
                    {ROLE_CONFIG[agenda.role]?.label || ''}
                  </Text>
                </HStack>
              </Box>
              <HStack gap={3} flexShrink={0} display={{ base: 'none', md: 'flex' }}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={ROLE_CONFIG[agenda.role]?.color || 'fg.muted'}
                  letterSpacing="wide"
                >
                  {ROLE_CONFIG[agenda.role]?.label || ''}
                </Text>
                <Badge colorPalette={agenda.is_active ? 'green' : 'gray'}>
                  {agenda.is_active ? 'Ativa' : 'Inativa'}
                </Badge>
              </HStack>
            </Box>
          </Card.Body>
        </HStack>
      )}
    </Link>
  </Card.Root>
);

export default AgendaCard
